<template>
  <div class="puff-map">
    <div class="device-alarm">
      <el-table :data="alarmData">
        <el-table-column
          v-for="item in alarmThead"
          :prop="item.prop"
          :key="item.prop"
          :label="item.label | translateTo(i18nPath)"
          align="center"
        />
      </el-table>
    </div>
    <Bmap ref="bmap" :initPoint="initPoint" :revisability="false" @mapHandler="mapHandler" />
  </div>
</template>

<script>
import Bmap from '@/components/bmap';
import { stateMapMarker } from '@/utils/dict';
import { getTopic, subscribe, unsubscribe } from '@/utils/mqtt';
import { translateTo } from '@/utils/locale';

export default {
  name: 'PuffMap',
  props: {
    taskId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      i18nPath: 'dataAnalysis',
      initPoint: {},
      mapLoading: null,
      timer: null,
      loading: false,
      opts: {},
      once: false,
      subscribeKey: getTopic(`{prefix}/{userId}/task/alarm/detail/${this.taskId}/`),
      alarmThead: [
        { label: '设备', prop: 'sn' },
        { label: '报警时间', prop: 'alarmDate' },
        { label: '报警内容', prop: 'alarmContent' }
      ],
      alarmData: []
    };
  },
  components: { Bmap },
  methods: {
    subscribe() {
      subscribe(this.subscribeKey);

      this.sockets.subscribe(this.subscribeKey, res => {
        this.updatePuff(res);
        this.upAlarmData(res);
      });
    },
    unsubscribe() {
      unsubscribe(this.subscribeKey);

      this.sockets.unsubscribe(this.subscribeKey);
      // TODO 取消报警扩散模型订阅
      // this.$socket.emit('/socket/unsubscribe/taskAlarmDetail', this.taskId);
    },
    upAlarmData(data) {
      try {
        data = JSON.parse(data.dataContent);
        if (!data || typeof data !== 'object') return;
        const keys = Object.keys(data);
        const res = keys
          .map(key => ({
            sn: key,
            alarmDate: data[key].alarmDate,
            alarmContent: data[key].alarmContent
          }))
          .filter(e => !!e.alarmDate);
        this.alarmData.splice(0, Infinity, ...res);
      } catch (e) {
        console.log('更新扩散模型数据异常: ', e);
      }
    },
    delPolygon(sn) {
      const target = (this.opts[sn] = this.opts[sn] || {});
      if (!(Array.isArray(target.polygons) && target.polygons.length)) return;
      target.polygons.forEach(e => e.clear());
    },
    delMarker(sn) {
      const target = (this.opts[sn] = this.opts[sn] || {});
      if (!target.marker) return;
      target.marker.remove();
    },
    addPolygon({ sn, _center, payload }) {
      this.delMarker(sn);
      const BMap = window.BMap;
      if (!BMap) return;
      let puffDataMap = new Map();
      for (let i = 0, len = payload.length; i < len; i++) {
        const { color, coords } = payload[i];
        let point1 = new BMap.Point(coords[0][0], coords[0][1]);
        let points = [];
        if (puffDataMap.has(color)) {
          points = puffDataMap.get(color);
        }
        points.push(point1);
        puffDataMap.set(color, points);
      }

      let target = (this.opts[sn] = this.opts[sn] || {});
      target.polygons = target.polygons || [];
      target.polygons.splice(0).forEach(e => {
        this.$refs['bmap'].bmap.removeOverlay(e);
        e.clear();
      });

      for (let key of puffDataMap.keys()) {
        const options = {
          // eslint-disable-next-line no-undef
          size: BMAP_POINT_SIZE_NORMAL,
          // eslint-disable-next-line no-undef
          shape: BMAP_POINT_SHAPE_CIRCLE,
          color: key
        };
        const colorPoints = new BMap.PointCollection(puffDataMap.get(key), options);
        target.polygons.push(colorPoints);
        this.$refs['bmap'].bmap.addOverlay(colorPoints);
      }
    },
    addLabel({ sn, center }) {
      const BMap = window.BMap;
      const target = (this.opts[sn] = this.opts[sn] || {});
      if (target.label) target.label.remove();
      target.label = new BMap.Label(sn, {
        position: new BMap.Point(center.xcoord, center.ycoord),
        offset: new BMap.Size(-30, -30)
      });
      this.$refs['bmap'].bmap.addOverlay(target.label);
    },
    addMarker({ sn, center }) {
      this.delPolygon(sn);
      const target = (this.opts[sn] = this.opts[sn] || {});
      const BMap = window.BMap;
      if (target.marker) target.marker.remove();
      target.marker = new BMap.Marker(new BMap.Point(center.xcoord, center.ycoord), {
        icon: new BMap.Icon(stateMapMarker[0], new BMap.Size(32, 32))
      });
      this.$refs['bmap'].bmap.addOverlay(target.marker);
    },
    updatePuff(res) {
      if (this.loading) return;
      this.loading = true;
      this.$nextTick(async () => {
        if (!res) return;
        if (!res.dataContent) {
          this.$message({
            type: 'warning',
            message: '暂无数据'
          });
          return;
        }
        const data = JSON.parse(res.dataContent);
        if (typeof data !== 'object') return;
        let center = null;
        let payload = null;
        Object.keys(data).forEach((sn, index) => {
          center = data[sn].center;
          payload = data[sn].payload;
          if (!(center && center.xcoord && center.ycoord)) return;
          if (!this.once && index === 0) {
            this.once = true;
            const BMap = window.BMap;
            this.initPoint = new BMap.Point(center.xcoord, center.ycoord);
            this.$refs['bmap'].bmap.centerAndZoom(this.initPoint, 16);
            this.$refs['bmap'].bmap.setMaxZoom(17);
          }
          center && this.addLabel({ sn, center });
          if (Array.isArray(payload) && payload.length) {
            this.addPolygon({ sn, center, payload });
          } else {
            this.addMarker({ sn, center });
          }
        });
        this.mapLoading && this.mapLoading.close();
      });
    },
    mapHandler(theme) {
      const BMap = window.BMap;
      this.initPoint = new BMap.Point(113.02296638, 31.3682952);
      this.$refs['bmap'].bmap.centerAndZoom(this.initPoint, 16);
      this.$refs['bmap'].bmap.setMaxZoom(17);
      if (theme) this.$refs['bmap'].bmap.setMapStyleV2({ styleJson: theme });
    }
  },
  mounted() {
    this.subscribe();
    this.mapLoading = this.$loading({
      lock: true,
      text: translateTo('绘制模型中', this.i18nPath),
      target: '.puff-map',
      background: '#0c162080'
    });
    this.timer = setInterval(() => {
      this.loading = false;
    }, 2000);
  },
  beforeDestroy() {
    this.unsubscribe();
    clearInterval(this.timer);
  }
};
</script>

<style lang="scss">
.puff-map {
  width: 100%;
  height: 78vh;
  position: relative;
  .device-alarm {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 99;
    width: 37%;
    opacity: 0.8;
  }
}
</style>
